import { createElement } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { startCase } from "lodash";
import axiosInstant from "../../../../api";
import { IVideo } from "../hooks/useFetchVideos";
import { IconCheck, IconCross, IconLink } from "../../../../utils/icons";

export const PAGE_SIZE = 20;
export const OptionMaxLimit = 40;

export const VIDEO_SORT_OPTIONS = [
    { value: "dateCreated", label: "Created Date" },
    { value: "name", label: "Name" },
    { value: "length", label: "Duration" }
];

export interface ISingleOption {
    id: string;
    name: string;
    image?: string;
    icon?: JSX.Element;
}

export const fetchVideoPage = async (
    searchParams: URLSearchParams,
    page: number,
    legacyMode?: boolean,
    brandId?: string | number,
    sort?: string,
    pageSize?: number,
    offsetOverride?: number,
    signal?: AbortSignal,
    // The admin "Deleted" view: only soft-deleted videos. The caller decides
    // (it checks the role); the API ignores the param for anyone else.
    deleted?: boolean
): Promise<{ rows: IVideo[]; count: number; error: string | null }> => {
    try {
        const limit = pageSize ?? PAGE_SIZE;
        // Callers that append (load-more) must offset by rows already held, not
        // by a page index — a short page would otherwise re-fetch seen rows.
        const offset = offsetOverride ?? (page - 1) * limit;

        const apiParams = new URLSearchParams(searchParams);
        apiParams.delete("limit");
        apiParams.delete("sort");
        apiParams.delete("page");
        apiParams.delete("view");
        apiParams.delete("video");
        apiParams.delete("selectedTab");
        apiParams.delete("nonProject");
        // `deleted=1` rides the URL for the section's toggle; it reaches the
        // API only through the `deleted` argument below.
        apiParams.delete("deleted");
        // The URL's story-filter key is `embed` (StoryFilter, story page); the
        // API only reads `embedId` — rename on the wire, as useFetchVideos did.
        const embedParam = apiParams.get("embed");
        if (embedParam) {
            apiParams.set("embedId", embedParam);
            apiParams.delete("embed");
        }

        const advancedSearch = apiParams.toString();
        let queryString = `?limit=${limit}&offset=${offset}${advancedSearch.length ? `&${advancedSearch}` : ""}`;
        if (sort) {
            queryString += `&sort=${encodeURIComponent(sort)}`;
        }
        if (legacyMode) {
            queryString += `&nonProjectOnly=true`;
        }
        if (deleted) {
            queryString += `&deleted=1`;
        }

        let endpoint: string;
        if (brandId) {
            endpoint = legacyMode
                ? `/brands/${brandId}/s3files/tdc${queryString}`
                : `/brands/${brandId}/videos${queryString}`;
        } else {
            endpoint = `accountmanager/videos/tdc${queryString}`;
        }

        const response = await axiosInstant(endpoint, { signal });
        const { status, data } = response;
        // A 200 with no `rows` key is an EMPTY result, not a failure: axios
        // rejects non-2xx (see the response interceptor), so genuine failures
        // land in catch below.
        if (status === 200) {
            return { rows: data?.rows ?? [], count: data?.count ?? 0, error: null };
        }
        return { rows: [], count: 0, error: "Request failed" };
    } catch (e) {
        if (axios.isCancel(e)) {
            // Superseded by a newer request — the caller's seq guard discards
            // this result; an intentional abort must not toast.
            return { rows: [], count: 0, error: null };
        }
        toast.error((e as Error).message);
        return { rows: [], count: 0, error: (e as Error).message };
    }
};

export const getFilterProps = (arr: Array<ISingleOption>, keyName: string, label?: string) => {
    const title = startCase((label || keyName).split("Id")[0]);
    return {
        placeholder: `Select ${title}`,
        options: arr.map((e) => e.name),
        optionsObjArray: arr,
        filterKey: keyName,
        title,
        boxStyle: { width: "300px" }
    };
};

const OPTION_ICON = { width: 16, height: 16, stroke: "#B0B0B0" };

export const YES_ICON = createElement(IconCheck, OPTION_ICON);
export const NO_ICON = createElement(IconCross, OPTION_ICON);
export const LINK_ICON = createElement(IconLink, OPTION_ICON);

export const extraFilters = [
    { id: "ad", name: "Ad", icon: YES_ICON },
    { id: "no-logo", name: "No Logo", icon: NO_ICON }
];

// Demo flag filter — proposal example/use-case videos (curated via the
// "Demo example" toggle in video settings). The backend matches demo=true.
export const demoFilters = [
    { id: "true", name: "Demo examples", icon: YES_ICON }
];

// PDP URL presence filter (hasPdp, 'true' | 'false'). The API evaluates it
// over the same per-video PDP set the card chip counts: the pages the
// video is on.
// Approved / not approved (URL key `approved`) — `final` is the approval.
export const approvalFilters = [
    { id: "true", name: "Approved" },
    { id: "false", name: "Not Approved" }
];

export const pdpUrlFilters = [
    { id: "true", name: "Has PDP URL", icon: LINK_ICON },
    { id: "false", name: "No PDP URL", icon: NO_ICON }
];

export const fancyTimeFormat = (duration: any) => {
    // Hours, minutes and seconds
    const hrs = ~~(duration / 3600);
    const mins = ~~((duration % 3600) / 60);
    const secs = ~~duration % 60;

    // Output like "1:01" or "4:03:59" or "123:03:59"
    let ret = "";

    if (hrs > 0) {
        ret += "" + hrs + ":" + (mins < 10 ? "0" : "");
    }

    ret += "" + mins + ":" + (secs < 10 ? "0" : "");
    ret += "" + secs;

    return ret;
};

// Does the path contain this whole SEGMENT (not just the substring)?
//
// These checks used to be `pathname.includes("/products")`, which was fine
// while every page sat at the root. Since TDC-1609 a brand client's pathname
// carries their slug (/portal/<slug>/videos), so a brand whose slug happens to
// contain "products" or "experts" would have matched by accident. Comparing
// whole segments removes the coincidence.
export const pathHasSegment = (pathname: string, segment: string): boolean =>
    String(pathname || "")
        .split("?")[0]
        .split("/")
        .filter(Boolean)
        .includes(segment);
